import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle } from 'lucide-react';
import { useApi, type TreeUsage } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/labels';
import { Card, UsageMeter } from '@/components/ui';
import { useOrgContext } from './layout';

/**
 * Members see what the organization means for them. Admins see a
 * getting-started checklist until it's done, then plan usage.
 */
export function OrgOverviewPage() {
  const { org, usage, role, canManage } = useOrgContext();

  if (!canManage) {
    return (
      <div className="space-y-6">
        <Card>
          {role ? (
            <>
              <p className="font-medium">
                You're a {ROLE_LABELS[role].toLowerCase()} in {org.name}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Courses and assessments from {org.name} appear on your dashboard and in Explore.
              </p>
              <Link
                to={`/learn?source=${org.id}`}
                className="-my-2 mt-2 inline-block py-2 text-sm font-medium text-primary"
              >
                See what {org.name} offers →
              </Link>
            </>
          ) : (
            <>
              <p className="font-medium">
                You're viewing {org.name} as an admin of its parent organization
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                You can see its members and usage. Its own admins manage it.
              </p>
            </>
          )}
        </Card>
        {!role && usage && <PlanUsage usage={usage} isRoot={!org.parentId} />}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GettingStarted orgId={org.id} usage={usage} />
      {usage && <PlanUsage usage={usage} isRoot={!org.parentId} />}
    </div>
  );
}

function GettingStarted({ orgId, usage }: { orgId: string; usage: TreeUsage | undefined }) {
  const api = useApi();
  const domains = useQuery({ queryKey: ['domains', orgId], queryFn: () => api.domains(orgId) });
  if (!usage || !domains.data) return null;

  const steps = [
    {
      done: usage.usage.maxMembers > 1,
      label: 'Invite your first members',
      hint: 'Share an invite code or link',
      to: 'invites',
    },
    {
      done: domains.data.domains.some((d) => d.verifiedAt),
      label: 'Verify your email domain',
      hint: 'People with your email domain can then join automatically',
      to: 'settings',
    },
    ...(usage.limits.maxChildOrgs > 0
      ? [
          {
            done: usage.usage.maxChildOrgs > 0,
            label: 'Add child organizations',
            hint: 'Colleges, departments or teams',
            to: 'children',
          },
        ]
      : []),
  ];
  if (steps.every((s) => s.done)) return null;

  return (
    <Card title="Get started">
      <ol className="space-y-3">
        {steps.map((s) => (
          <li key={s.label}>
            <Link to={s.to} className="flex items-start gap-3 rounded-lg p-2 hover:bg-muted">
              {s.done ? (
                <CheckCircle2
                  className="mt-0.5 size-5 shrink-0 text-india-green"
                  aria-label="Done"
                />
              ) : (
                <Circle
                  className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                  aria-label="To do"
                />
              )}
              <span>
                <span
                  className={`block font-medium ${s.done ? 'text-muted-foreground line-through' : ''}`}
                >
                  {s.label}
                </span>
                <span className="block text-sm text-muted-foreground">{s.hint}</span>
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </Card>
  );
}

const LIMIT_LABELS = {
  maxMembers: 'Members',
  maxChildOrgs: 'Child organizations',
  maxAdmins: 'Admins',
  maxDomains: 'Email domains',
  maxStorageMb: 'Course file storage (MB)',
  maxAiDraftsPerMonth: 'AI drafts this month',
} as const;

function PlanUsage({ usage, isRoot }: { usage: TreeUsage; isRoot: boolean }) {
  const keys = (Object.keys(LIMIT_LABELS) as Array<keyof typeof LIMIT_LABELS>).filter(
    (k) => k !== 'maxChildOrgs' || usage.limits.maxChildOrgs > 0,
  );
  return (
    <Card title={`${usage.planName} plan`}>
      <div className="grid gap-4 sm:grid-cols-2">
        {keys.map((k) => (
          <UsageMeter
            key={k}
            label={LIMIT_LABELS[k]}
            used={usage.usage[k]}
            limit={usage.limits[k]}
          />
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        {isRoot
          ? 'Limits cover this organization and all its child organizations.'
          : `Limits are shared across ${usage.rootOrgName}.`}{' '}
        Need more? Contact the Nirvik team.
      </p>
    </Card>
  );
}
